// File: typescript/apps/front-end/src/main/react/distinction/DistinctionCelebrationSlot.tsx
import React, { Suspense } from "react";
import type { DistinctionCelebrationDTO } from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";

/**
 * The card itself is code-split (rule 26): it is needed on a tiny fraction of home visits, and only
 * after the celebration GET has answered, so it never belongs on the path to first paint. This slot
 * holds no catalog key literal on purpose, so nothing here pulls distinction copy into a hotter catalog.
 */
const DistinctionCelebrationCardImpl = lazyWithRetry(
    () =>
        import("@/main/react/distinction/DistinctionCelebrationCard").then((m) => ({
            default: m.DistinctionCelebrationCard,
        })),
    "distinction-celebration-card",
);

/**
 * Home-page host for the one-time distinction card (distinctions design §5, A10). Mounted for a
 * signed-in member only: the celebration endpoint is owner-only and not in the anonymous allowlist.
 *
 * <p>Asks once per mount. Renders nothing while it waits, when there is nothing to celebrate, and when
 * the request fails: the card is purely additive, so there is no server HTML for it to subtract from
 * (rule 32), and the failure is logged rather than swallowed.
 */
export function DistinctionCelebrationSlot({ localizer }: { localizer: Localizer }) {
    const [celebration, setCelebration] = React.useState<DistinctionCelebrationDTO | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        ClientFactory.getDistinctionCelebration()
            .then((response) => {
                if (!cancelled) {
                    setCelebration(response?.data?.celebration ?? null);
                }
            })
            .catch((error) => {
                logger.warn("Failed to load the distinction celebration", error);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!celebration) {
        return null;
    }

    return (
        <LazyControlBoundary controlName="distinction-celebration-card" fallback={null}>
            <Suspense fallback={null}>
                <DistinctionCelebrationCardImpl
                    localizer={localizer}
                    celebration={celebration}
                    onDone={() => setCelebration(null)}
                />
            </Suspense>
        </LazyControlBoundary>
    );
}

export default DistinctionCelebrationSlot;
